/**
 * ParameterDetailPage - Unified view for a single parameter.
 *
 * Accessible via /parameters/:parameterKey (e.g., /parameters/no3)
 *
 * Shows:
 * - Header with current value, target range, status
 * - Time series chart with target range band
 * - Editable data table with exclude toggle
 * - "Log Reading" button opening the manual entry modal
 * - "Compare in Analysis" link
 */
import { useState, useMemo, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  Container,
  Stack,
  Group,
  Title,
  Text,
  Button,
  Paper,
  Box,
  Alert,
  ActionIcon,
  Badge,
  Tooltip,
  SegmentedControl,
  Checkbox,
  Skeleton,
  Anchor,
  Loader,
  Center,
  Pagination,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconArrowLeft,
  IconPlus,
  IconChartLine,
  IconCircleCheck,
  IconAlertTriangle,
  IconArrowUp,
  IconEdit,
  IconEyeOff,
  IconNote,
} from '@tabler/icons-react';
import { useChartAnnotations } from '@/hooks/useChartAnnotations';
import { ChartAnnotationLayer } from '@/components/charts/ChartAnnotationLayer';
import { AddAnnotationModal } from '@/components/charts/AddAnnotationModal';
import type { ChartAnnotation } from '@/types/chartAnnotation';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceArea,
} from 'recharts';
import { format, subDays, subYears } from 'date-fns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useTank } from '@/contexts/TankContext';
import { NoTankSelected } from '@/components/common/NoTankSelected';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useAnalysisData } from '@/hooks/useAnalysisData';
import { useTankTargetRanges } from '@/api/measurements';
import { CACHE_TIMES } from '@/config/cacheTimes';
import { isWithinRange } from '@/hooks/useTargetRanges';
import { getParameterConfig, parseParameterKey, getSourceDisplayName } from '@/features/analysis/constants';
import { CHART_GRID_STROKE, CHART_AXIS_TICK } from '@/utils/chartStyles';
import {
  TooltipContainer,
  TooltipHeader,
  TooltipRow,
  TooltipNote,
  formatMeasurementValue,
} from '@/features/analysis/components/ChartTooltip';
import { api as dataEditorApi } from '@/api/dataEditor';
import { TESTER_BRANDS } from '@/api/measurements';
import { buildApiUrl } from '@/api/config';
import { apiClient } from '@/api/client';
import { UnifiedManualEntryModal } from '@/components/UnifiedManualEntryModal/UnifiedManualEntryModal';
import { EditManualMeasurementModal } from '@/features/dataEditor/EditManualMeasurementModal';
import { NoteEditCell } from '@/features/dataEditor/NoteEditCell';
import { DosingHistorySection } from '@/features/dosing/DosingHistorySection';
import type { DataPoint } from '@/types/dataEditor';
import type { DataEditorResponse } from '@/types/dataEditor';
import { formatDistanceToNow } from 'date-fns';

// ─── Parameter display config (precision + rangeKey for tile parity) ───
const PARAM_PRECISION: Record<string, number> = {
  temp: 1,
  ph: 2,
  salinity: 1,
  alkalinity: 1,
  ca: 0,
  mg: 0,
  no3: 1,
  po4: 2,
};

// Map parameter keys to target range IDs (matches MetricTilesGrid.TILE_CONFIGS.rangeKey)
const PARAM_RANGE_KEY: Record<string, string> = {
  temp: 'temperature',
  ph: 'ph',
  salinity: 'salinity',
  alkalinity: 'alkalinity',
  ca: 'calcium',
  mg: 'magnesium',
  no3: 'nitrate',
  po4: 'phosphate',
};

// ─── Time range options ───
type TimeRange = '7d' | '30d' | '90d' | '1y' | 'all';

const TIME_RANGE_OPTIONS: Array<{ value: TimeRange; label: string }> = [
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: '90d', label: '90D' },
  { value: '1y', label: '1Y' },
  { value: 'all', label: 'All' },
];

function getDateRangeForTimeRange(range: TimeRange): [string, string] {
  const end = new Date();
  const endStr = format(end, 'yyyy-MM-dd');

  switch (range) {
    case '7d':
      return [format(subDays(end, 7), 'yyyy-MM-dd'), endStr];
    case '30d':
      return [format(subDays(end, 30), 'yyyy-MM-dd'), endStr];
    case '90d':
      return [format(subDays(end, 90), 'yyyy-MM-dd'), endStr];
    case '1y':
      return [format(subYears(end, 1), 'yyyy-MM-dd'), endStr];
    case 'all':
      return ['2020-01-01', endStr];
  }
}

// ─── Data fetching ───
async function fetchParameterData(
  parameter: string,
  tankId: string,
  startDate: string,
  endDate: string,
): Promise<DataEditorResponse> {
  const params = new URLSearchParams({
    parameter,
    exclude_outliers: 'false',
    start_date: startDate,
    end_date: endDate,
  });

  const response = await apiClient(
    buildApiUrl(`/api/tanks/${tankId}/outliers?${params}`)
  );

  if (!response.ok) {
    throw new Error('Failed to fetch parameter data');
  }

  return response.json();
}

// ─── Chart tooltip (multi-source) ───
function MultiSourceTooltip({
  active,
  payload,
  label,
  paramKey,
  unit,
  sourceColors,
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number | undefined; color: string; payload?: Record<string, unknown> }>;
  label?: number;
  paramKey: string;
  unit: string;
  sourceColors: Record<string, string>;
}): JSX.Element | null {
  if (!active || !payload || payload.length === 0 || !label) {
    return null;
  }

  // Filter to only entries that have a value at this timestamp
  const validEntries = payload.filter(
    (p) => p.value != null && typeof p.value === 'number'
  );
  if (validEntries.length === 0) return null;

  return (
    <TooltipContainer>
      <TooltipHeader timestamp={label} showTime />
      {validEntries.map((entry) => {
        const compositeKey = entry.dataKey;
        const { source } = parseParameterKey(compositeKey);
        const color = sourceColors[compositeKey] ?? entry.color;
        const note = entry.payload?.[`${compositeKey}_note`] as string | undefined;
        return (
          <div key={compositeKey}>
            <TooltipRow
              color={color}
              label={getSourceDisplayName(source ?? compositeKey)}
              value={formatMeasurementValue(entry.value!, paramKey)}
              unit={unit}
            />
            {note && <TooltipNote note={note} />}
          </div>
        );
      })}
    </TooltipContainer>
  );
}

/**
 * Six visually distinct colors for per-source chart lines.
 * Assigned by index so no two sources on the same chart ever share a color.
 */
const SOURCE_LINE_COLORS = [
  'hsl(210, 80%, 50%)',  // Blue
  'hsl(30, 95%, 55%)',   // Orange
  'hsl(145, 65%, 40%)',  // Green
  'hsl(270, 60%, 55%)',  // Purple
  'hsl(0, 70%, 50%)',    // Red
  'hsl(185, 85%, 42%)',  // Cyan
] as const;

/** Get a stable, unique color for a source by its position in the sorted list. */
function getSourceColor(_compositeKey: string, index: number): string {
  return SOURCE_LINE_COLORS[index % SOURCE_LINE_COLORS.length] ?? SOURCE_LINE_COLORS[0];
}

// ─── Main page component ───
export default function ParameterDetailPage() {
  const { parameterKey } = useParams<{ parameterKey: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedTank } = useTank();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  // Modal state
  const [logModalOpened, { open: openLogModal, close: closeLogModal }] = useDisclosure(false);
  const [editingMeasurement, setEditingMeasurement] = useState<DataPoint | null>(null);

  // Annotation modal state
  const [annotationModalOpened, { open: openAnnotationModal, close: closeAnnotationModal }] = useDisclosure(false);
  const [selectedAnnotation, setSelectedAnnotation] = useState<ChartAnnotation | null>(null);

  // Source visibility (composite keys that are hidden; empty = all visible)
  const [hiddenSources, setHiddenSources] = useState<Set<string>>(new Set());

  // Time range from URL (default: 30d)
  const timeRange = (searchParams.get('range') as TimeRange) || '30d';
  const setTimeRange = useCallback(
    (range: TimeRange) => {
      setSearchParams(
        (prev) => {
          if (range === '30d') {
            prev.delete('range');
          } else {
            prev.set('range', range);
          }
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Resolve parameter config
  const paramConfig = parameterKey ? getParameterConfig(parameterKey) : null;
  const precision = PARAM_PRECISION[parameterKey ?? ''] ?? 2;
  const rangeKey = PARAM_RANGE_KEY[parameterKey ?? ''];

  // Compute date range
  const [startDate, endDate] = useMemo(
    () => getDateRangeForTimeRange(timeRange),
    [timeRange]
  );

  // ─── Data hooks ───

  // Chart data from analysis endpoint (includes all sources)
  const { data: analysisData, isLoading: chartLoading } = useAnalysisData({
    tankId: selectedTank?.id,
    startDate,
    endDate,
    parameters: parameterKey ? [parameterKey] : [],
    includeDosing: true,
    includeEvents: false,
  });

  // Target ranges
  const { data: targetRangesData } = useTankTargetRanges(selectedTank?.id);

  // Table data from outliers endpoint
  const { data: tableData, isLoading: tableLoading } = useQuery({
    queryKey: ['parameter-detail', selectedTank?.id, parameterKey, startDate, endDate],
    queryFn: () => fetchParameterData(parameterKey!, selectedTank!.id, startDate, endDate),
    enabled: !!selectedTank?.id && !!parameterKey,
    staleTime: CACHE_TIMES.DATA,
  });

  // Fetch annotations for this parameter
  const { data: annotations = [] } = useChartAnnotations(selectedTank?.id, {
    startDate: new Date(startDate),
    endDate: new Date(endDate),
    parameter: parameterKey,
  });

  // Filter annotations for this specific parameter
  const filteredAnnotations = useMemo(() => {
    return annotations.filter(
      (a) => !a.parameter || a.parameter === parameterKey
    );
  }, [annotations, parameterKey]);

  // ─── Derived data ───

  // Target range for this parameter
  const targetRange = useMemo(() => {
    if (!rangeKey || !targetRangesData?.ranges) return null;
    return targetRangesData.ranges.find((r) => r.parameterId === rangeKey) ?? null;
  }, [rangeKey, targetRangesData]);

  // All measurement points grouped by source composite key
  const chartDataBySource = useMemo(() => {
    if (!analysisData?.measurements || !parameterKey) return {};

    const result: Record<string, Array<{ timestamp: number; value: number; device: string; note?: string }>> = {};

    for (const [compositeKey, points] of Object.entries(analysisData.measurements)) {
      const { param } = parseParameterKey(compositeKey);
      if (param !== parameterKey) continue;

      result[compositeKey] = points
        .map((p) => ({
          timestamp: new Date(p.timestamp).getTime(),
          value: p.value,
          device: p.device,
          note: p.note,
        }))
        .sort((a, b) => a.timestamp - b.timestamp);
    }

    return result;
  }, [analysisData, parameterKey]);

  // Available source keys (sorted for stable ordering)
  const availableSources = useMemo(
    () => Object.keys(chartDataBySource).sort(),
    [chartDataBySource]
  );

  // Visible source keys (all except hidden)
  const visibleSources = useMemo(
    () => availableSources.filter((k) => !hiddenSources.has(k)),
    [availableSources, hiddenSources]
  );

  // Stable color map: compositeKey -> color
  const sourceColors = useMemo(() => {
    const map: Record<string, string> = {};
    availableSources.forEach((key, i) => {
      map[key] = getSourceColor(key, i);
    });
    return map;
  }, [availableSources]);

  // Extract source names from composite keys for the annotation modal
  const sourceNamesForModal = useMemo(() => {
    if (availableSources.length <= 1) return undefined;
    return availableSources.map(key => parseParameterKey(key).source).filter(Boolean) as string[];
  }, [availableSources]);

  // Unified chart data: merge all sources into one array with per-source value columns.
  // Each row: { timestamp, "alkalinity:mastertronic": 8.2, "alkalinity:manual": undefined, ... }
  // Notes stored as "${compositeKey}_note" keys for tooltip access.
  const unifiedChartData = useMemo(() => {
    const pointMap = new Map<number, Record<string, number | string | undefined>>();

    for (const compositeKey of visibleSources) {
      const points = chartDataBySource[compositeKey];
      if (!points) continue;

      for (const pt of points) {
        let row = pointMap.get(pt.timestamp);
        if (!row) {
          row = { timestamp: pt.timestamp } as Record<string, number | string | undefined>;
          pointMap.set(pt.timestamp, row);
        }
        row[compositeKey] = pt.value;
        if (pt.note) {
          row[`${compositeKey}_note`] = pt.note;
        }
      }
    }

    return Array.from(pointMap.values()).sort(
      (a, b) => (a.timestamp as number) - (b.timestamp as number)
    );
  }, [chartDataBySource, visibleSources]);

  // Total data point count across all sources (for empty state check)
  const totalChartPoints = useMemo(() => {
    let count = 0;
    for (const points of Object.values(chartDataBySource)) {
      count += points.length;
    }
    return count;
  }, [chartDataBySource]);

  // Latest value from all sources (most recent regardless of source)
  const latestPoint = useMemo(() => {
    let latest: { timestamp: number; value: number; device: string } | null = null;
    for (const points of Object.values(chartDataBySource)) {
      const last = points[points.length - 1];
      if (last && (!latest || last.timestamp > latest.timestamp)) {
        latest = last;
      }
    }
    return latest;
  }, [chartDataBySource]);

  // Range status
  const rangeStatus = useMemo(() => {
    if (!latestPoint || !targetRange) return 'unknown' as const;
    return isWithinRange(latestPoint.value, {
      min_value: targetRange.minValue,
      max_value: targetRange.maxValue,
    });
  }, [latestPoint, targetRange]);

  // Y-axis domain with padding (computed from visible sources only)
  const yDomain = useMemo((): [number, number] => {
    if (unifiedChartData.length === 0) return [0, 10];
    const values: number[] = [];
    for (const row of unifiedChartData) {
      for (const key of visibleSources) {
        const v = row[key];
        if (v != null) values.push(v as number);
      }
    }
    if (values.length === 0) return [0, 10];
    // Include target range in domain if present
    const allValues = [...values];
    if (targetRange?.minValue != null) allValues.push(targetRange.minValue);
    if (targetRange?.maxValue != null) allValues.push(targetRange.maxValue);
    const domainMin = Math.min(...allValues);
    const domainMax = Math.max(...allValues);
    const padding = (domainMax - domainMin) * 0.1 || 1;
    return [Math.max(0, domainMin - padding), domainMax + padding];
  }, [unifiedChartData, visibleSources, targetRange]);

  // Time domain
  const timeDomain = useMemo(() => {
    return {
      startTs: new Date(`${startDate}T00:00:00`).getTime(),
      endTs: new Date(`${endDate}T23:59:59.999`).getTime(),
    };
  }, [startDate, endDate]);

  // Table rows (sorted newest first)
  const tableRows = useMemo(() => {
    if (!tableData?.data) return [];
    return [...tableData.data].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [tableData]);

  // Pagination
  const PAGE_SIZE = 20;
  const [tablePage, setTablePage] = useState(1);
  const totalPages = Math.ceil(tableRows.length / PAGE_SIZE);
  const paginatedRows = useMemo(
    () => tableRows.slice((tablePage - 1) * PAGE_SIZE, tablePage * PAGE_SIZE),
    [tableRows, tablePage]
  );

  // ─── Mutations ───

  // Toggle exclude
  const excludeMutation = useMutation({
    mutationFn: async ({ row, excluded }: { row: DataPoint; excluded: boolean }) => {
      if (row.measurement_id) {
        return dataEditorApi.updateOutlier(selectedTank!.id, {
          measurement_id: row.measurement_id,
          excluded,
        });
      }
      return dataEditorApi.updateOutlier(selectedTank!.id, {
        table_name: row.table_name,
        timestamp: row.timestamp,
        excluded,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parameter-detail'] });
      queryClient.invalidateQueries({ queryKey: ['analysis'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['latest-metrics'] });
    },
  });

  // Inline note update
  const noteMutation = useMutation({
    mutationFn: async ({ measurementId, note }: { measurementId: number; note: string }) => {
      return dataEditorApi.updateNote(selectedTank!.id, {
        measurement_id: measurementId,
        note,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parameter-detail'] });
    },
  });

  // Check if manual entries exist (for showing Test Kit column)
  const hasManualEntries = useMemo(
    () => tableRows.some((row) => row.source === 'manual'),
    [tableRows]
  );

  // ─── Annotation handlers ───

  const handleAddNote = useCallback(() => {
    setSelectedAnnotation(null);
    openAnnotationModal();
  }, [openAnnotationModal]);

  const handleEditAnnotation = useCallback((annotation: ChartAnnotation) => {
    setSelectedAnnotation(annotation);
    openAnnotationModal();
  }, [openAnnotationModal]);

  // ─── Guards ───

  if (!parameterKey || !paramConfig) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" title="Unknown Parameter">
          Parameter &quot;{parameterKey}&quot; is not recognized.
          <br />
          <Anchor component={Link} to="/" mt="sm">
            Return to Dashboard
          </Anchor>
        </Alert>
      </Container>
    );
  }

  if (!selectedTank) {
    return (
      <Container size="lg" py="xl">
        <NoTankSelected purpose="to view parameter details" />
      </Container>
    );
  }

  const unit = paramConfig.unit;

  return (
    <Container size="lg" py="md">
      <Stack gap="lg">
        {/* ─── Header ─── */}
        <Box>
          <Group gap="xs" mb="xs">
            <ActionIcon
              component={Link}
              to="/"
              variant="subtle"
              aria-label="Back to Dashboard"
            >
              <IconArrowLeft size={18} />
            </ActionIcon>
            <Text size="sm" c="dimmed">
              Dashboard
            </Text>
          </Group>

          <Group justify="space-between" align="flex-start" wrap="wrap">
            <Box>
              <Title order={2}>{paramConfig.label}</Title>
              <Group gap="sm" mt={4}>
                {latestPoint ? (
                  <>
                    <Text
                      size="xl"
                      fw={700}
                      style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                      c={rangeStatus === 'below' || rangeStatus === 'above' ? 'var(--rdh-accent)' : undefined}
                    >
                      {latestPoint.value.toFixed(precision)} {unit}
                    </Text>
                    {rangeStatus === 'in-range' && (
                      <Tooltip label={`Target: ${targetRange?.minValue?.toFixed(precision)} – ${targetRange?.maxValue?.toFixed(precision)} ${unit}`}>
                        <Badge
                          color="teal"
                          variant="light"
                          leftSection={<IconCircleCheck size={14} />}
                        >
                          In Range
                        </Badge>
                      </Tooltip>
                    )}
                    {rangeStatus === 'below' && (
                      <Tooltip label={`Below target: ${targetRange?.minValue?.toFixed(precision)} ${unit}`}>
                        <Badge
                          color="red"
                          variant="light"
                          leftSection={<IconAlertTriangle size={14} />}
                        >
                          Below Target
                        </Badge>
                      </Tooltip>
                    )}
                    {rangeStatus === 'above' && (
                      <Tooltip label={`Above target: ${targetRange?.maxValue?.toFixed(precision)} ${unit}`}>
                        <Badge
                          color="red"
                          variant="light"
                          leftSection={<IconArrowUp size={14} />}
                        >
                          Above Target
                        </Badge>
                      </Tooltip>
                    )}
                    <Text size="sm" c="dimmed">
                      {formatDistanceToNow(new Date(latestPoint.timestamp), { addSuffix: true })}
                    </Text>
                  </>
                ) : chartLoading ? (
                  <Skeleton width={120} height={28} />
                ) : (
                  <Text c="dimmed">No readings yet</Text>
                )}
              </Group>
            </Box>

            <Button
              leftSection={<IconPlus size={16} />}
              onClick={openLogModal}
            >
              Log Reading
            </Button>
          </Group>
        </Box>

        {/* ─── Chart Section ─── */}
        <Paper p="md" withBorder>
          <Group justify="space-between" mb="md" wrap="wrap">
            <SegmentedControl
              size="xs"
              value={timeRange}
              onChange={(value) => setTimeRange(value as TimeRange)}
              data={TIME_RANGE_OPTIONS}
            />
            <Anchor
              component={Link}
              to={`/analysis?${visibleSources.map((k) => `params=${encodeURIComponent(k)}`).join('&')}`}
              size="sm"
              c="dimmed"
            >
              <Group gap={4} wrap="nowrap">
                <IconChartLine size={14} />
                Compare in Analysis
              </Group>
            </Anchor>
          </Group>

          {/* Source toggles (only show when multiple sources exist) */}
          {availableSources.length > 1 && (
            <Group gap="xs" mb="sm">
              <Text size="xs" c="dimmed" fw={500}>Sources:</Text>
              {availableSources.map((compositeKey) => {
                const { source } = parseParameterKey(compositeKey);
                const isVisible = !hiddenSources.has(compositeKey);
                const color = sourceColors[compositeKey] ?? 'gray';
                return (
                  <Badge
                    key={compositeKey}
                    size="sm"
                    variant={isVisible ? 'light' : 'outline'}
                    color={isVisible ? undefined : 'gray'}
                    leftSection={
                      isVisible
                        ? <Box style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: color }} />
                        : <IconEyeOff size={12} />
                    }
                    style={{ cursor: 'pointer', opacity: isVisible ? 1 : 0.5 }}
                    onClick={() => {
                      setHiddenSources((prev) => {
                        const next = new Set(prev);
                        if (next.has(compositeKey)) {
                          next.delete(compositeKey);
                        } else {
                          // Don't allow hiding all sources
                          if (visibleSources.length > 1) {
                            next.add(compositeKey);
                          }
                        }
                        return next;
                      });
                    }}
                  >
                    {getSourceDisplayName(source ?? compositeKey)}
                  </Badge>
                );
              })}
            </Group>
          )}

          {/* Add Note button */}
          <Group justify="flex-end" mb="xs">
            <Tooltip label="Add note" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={handleAddNote}
                aria-label="Add note to chart"
              >
                <IconNote size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>

          {chartLoading ? (
            <Center h={250}>
              <Loader size="sm" />
            </Center>
          ) : unifiedChartData.length >= 2 ? (
            <ResponsiveContainer width="100%" height={isMobile ? 200 : 280}>
              <LineChart
                data={unifiedChartData}
                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  domain={[timeDomain.startTs, timeDomain.endTs]}
                  tickFormatter={(value) => format(new Date(value), isMobile ? 'M/d' : 'MMM d')}
                  tick={CHART_AXIS_TICK}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  domain={yDomain}
                  tick={CHART_AXIS_TICK}
                  width={50}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) => formatMeasurementValue(value, parameterKey)}
                />
                {/* Target range band */}
                {targetRange?.minValue != null && targetRange?.maxValue != null && (
                  <ReferenceArea
                    y1={targetRange.minValue}
                    y2={targetRange.maxValue}
                    fill="hsl(162, 73%, 46%)"
                    fillOpacity={0.08}
                    stroke="hsl(162, 73%, 46%)"
                    strokeOpacity={0.2}
                    strokeDasharray="3 3"
                  />
                )}
                <RechartsTooltip
                  content={
                    <MultiSourceTooltip
                      paramKey={parameterKey}
                      unit={unit}
                      sourceColors={sourceColors}
                    />
                  }
                  cursor={{ stroke: 'hsl(210, 20%, 70%)', strokeWidth: 1, strokeDasharray: '4 4' }}
                />
                {/* One line per visible source */}
                {visibleSources.map((compositeKey) => {
                  const color = sourceColors[compositeKey] ?? 'hsl(210, 80%, 50%)';
                  return (
                    <Line
                      key={compositeKey}
                      type="monotone"
                      dataKey={compositeKey}
                      stroke={color}
                      strokeWidth={2}
                      dot={{ r: 2, fill: color }}
                      activeDot={{ r: 4 }}
                      connectNulls
                      name={compositeKey}
                    />
                  );
                })}
                {/* Annotation overlays */}
                <ChartAnnotationLayer
                  annotations={filteredAnnotations}
                  onAnnotationClick={handleEditAnnotation}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : totalChartPoints === 1 ? (
            <Center h={120}>
              <Stack align="center" gap="xs">
                <Text size="xl" fw={700} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {latestPoint?.value.toFixed(precision)} {unit}
                </Text>
                <Text size="sm" c="dimmed">
                  Only one data point — chart needs at least two readings
                </Text>
              </Stack>
            </Center>
          ) : (
            <Center h={120}>
              <Stack align="center" gap="xs">
                <Text c="dimmed">No data for this time range</Text>
                <Button variant="light" size="xs" onClick={openLogModal} leftSection={<IconPlus size={14} />}>
                  Log your first {paramConfig.label} reading
                </Button>
              </Stack>
            </Center>
          )}
        </Paper>

        {/* ─── Dosing History ─── */}
        <DosingHistorySection
          parameterKey={parameterKey}
          dosing={analysisData?.dosing ?? {}}
          availableDosing={analysisData?.available_dosing ?? []}
          isLoading={chartLoading}
          isMobile={isMobile}
          timeDomain={timeDomain}
        />

        {/* ─── Data Table ─── */}
        <Paper p="md" withBorder>
          <Group justify="space-between" mb="md">
            <Title order={4}>Readings</Title>
            <Text size="sm" c="dimmed">
              {tableRows.length} {tableRows.length === 1 ? 'reading' : 'readings'}
            </Text>
          </Group>

          {tableLoading ? (
            <Stack gap="xs">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={40} />
              ))}
            </Stack>
          ) : tableRows.length === 0 ? (
            <Center py="xl">
              <Stack align="center" gap="xs">
                <Text c="dimmed">No readings in this time range</Text>
                <Button variant="light" size="xs" onClick={openLogModal} leftSection={<IconPlus size={14} />}>
                  Log a reading
                </Button>
              </Stack>
            </Center>
          ) : isMobile ? (
            /* Mobile: card layout */
            <Stack gap="xs">
              {paginatedRows.map((row) => (
                <Paper
                  key={row.measurement_id ?? row.timestamp}
                  p="sm"
                  withBorder
                  style={{
                    backgroundColor: row.excluded ? 'var(--mantine-color-red-0)' : undefined,
                    opacity: row.excluded ? 0.6 : 1,
                  }}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Box style={{ flex: 1 }}>
                      <Group gap="xs" mb={4}>
                        <Text fw={600}>
                          {row.value.toFixed(precision)} {unit}
                        </Text>
                        <Badge size="xs" variant="light" color="gray">
                          {getSourceDisplayName(row.source)}
                        </Badge>
                        {row.excluded && (
                          <Badge size="xs" color="red" variant="light">
                            Excluded
                          </Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {format(new Date(row.timestamp), "MMM d, yyyy 'at' h:mm a")}
                      </Text>
                    </Box>
                    <Group gap="xs" wrap="nowrap">
                      <Checkbox
                        size="xs"
                        checked={row.excluded}
                        onChange={() =>
                          excludeMutation.mutate({ row, excluded: !row.excluded })
                        }
                        label="Excl"
                        styles={{ label: { fontSize: 11, paddingLeft: 4 } }}
                      />
                      {row.source === 'manual' && row.measurement_id && (
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => setEditingMeasurement(row)}
                          aria-label="Edit measurement"
                        >
                          <IconEdit size={14} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Group>
                  {/* Inline note (mobile) */}
                  <Box mt="xs">
                    <NoteEditCell
                      value={row.note || ''}
                      onSave={(newNote) =>
                        noteMutation.mutate({
                          measurementId: row.measurement_id,
                          note: newNote,
                        })
                      }
                      disabled={noteMutation.isPending}
                    />
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : (
            /* Desktop: table layout matching data editor */
            <Box style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, textAlign: 'center', width: 80 }}>Exclude</th>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Value</th>
                    <th style={thStyle}>Source</th>
                    {hasManualEntries && <th style={thStyle}>Test Kit</th>}
                    <th style={{ ...thStyle, minWidth: 200 }}>Note</th>
                    <th style={{ ...thStyle, textAlign: 'center', width: 60 }}>Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((row) => (
                    <tr
                      key={row.measurement_id ?? row.timestamp}
                      style={{
                        backgroundColor: row.excluded ? 'var(--mantine-color-red-0)' : undefined,
                        opacity: row.excluded ? 0.6 : 1,
                        borderBottom: '1px solid var(--mantine-color-gray-2)',
                      }}
                    >
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <Checkbox
                          size="xs"
                          checked={row.excluded}
                          onChange={() =>
                            excludeMutation.mutate({ row, excluded: !row.excluded })
                          }
                          aria-label={row.excluded ? 'Include this reading' : 'Exclude this reading'}
                        />
                      </td>
                      <td style={tdStyle}>
                        <Text size="sm">
                          {format(new Date(row.timestamp), "MMM d, yyyy")}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {format(new Date(row.timestamp), "h:mm a")}
                        </Text>
                      </td>
                      <td style={tdStyle}>
                        <Text size="sm" fw={500}>
                          {row.value.toFixed(precision)} {unit}
                        </Text>
                      </td>
                      <td style={tdStyle}>
                        <Badge size="sm" variant="light">
                          {getSourceDisplayName(row.source)}
                        </Badge>
                      </td>
                      {hasManualEntries && (
                        <td style={tdStyle}>
                          {row.source === 'manual' && row.tester_brand ? (
                            <Badge variant="outline" size="sm" color="grape">
                              {TESTER_BRANDS.find((t) => t.value === row.tester_brand)?.label || row.tester_brand}
                            </Badge>
                          ) : (
                            <Text size="sm" c="dimmed">—</Text>
                          )}
                        </td>
                      )}
                      <td style={tdStyle}>
                        <NoteEditCell
                          value={row.note || ''}
                          onSave={(newNote) =>
                            noteMutation.mutate({
                              measurementId: row.measurement_id,
                              note: newNote,
                            })
                          }
                          disabled={noteMutation.isPending}
                        />
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        {row.source === 'manual' && row.measurement_id ? (
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => setEditingMeasurement(row)}
                            aria-label="Edit measurement"
                          >
                            <IconEdit size={14} />
                          </ActionIcon>
                        ) : (
                          <Text size="xs" c="dimmed">—</Text>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Group justify="center" mt="md">
              <Pagination
                value={tablePage}
                onChange={setTablePage}
                total={totalPages}
                size="sm"
              />
            </Group>
          )}
        </Paper>
      </Stack>

      {/* ─── Log Reading Modal ─── */}
      <UnifiedManualEntryModal
        opened={logModalOpened}
        onClose={() => {
          closeLogModal();
          // Refresh data after logging
          queryClient.invalidateQueries({ queryKey: ['parameter-detail'] });
          queryClient.invalidateQueries({ queryKey: ['analysis'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard'] });
          queryClient.invalidateQueries({ queryKey: ['latest-metrics'] });
        }}
        initialType="water-test"
        defaultParameterKey={parameterKey}
      />

      {/* ─── Edit Measurement Modal ─── */}
      <EditManualMeasurementModal
        opened={!!editingMeasurement}
        onClose={() => {
          setEditingMeasurement(null);
          queryClient.invalidateQueries({ queryKey: ['parameter-detail'] });
          queryClient.invalidateQueries({ queryKey: ['analysis'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard'] });
          queryClient.invalidateQueries({ queryKey: ['latest-metrics'] });
        }}
        dataPoint={editingMeasurement}
        parameterKey={parameterKey}
        tankId={selectedTank.id}
      />

      {/* Annotation Modal */}
      <AddAnnotationModal
        opened={annotationModalOpened}
        onClose={closeAnnotationModal}
        tankId={selectedTank?.id}
        chartContext={{
          parameter: parameterKey || '',
          parameterLabel: paramConfig?.label || parameterKey || '',
          sources: sourceNamesForModal,
        }}
        existingAnnotation={selectedAnnotation}
      />
    </Container>
  );
}

// ─── Table styles ───
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--mantine-color-dimmed)',
  borderBottom: '2px solid var(--mantine-color-gray-3)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  verticalAlign: 'top',
};
